import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  adminUsersBulkImportApi,
  type BulkImportPreviewResponse,
  type BulkImportResponse,
  type BulkImportRow,
  type BulkImportResultRow,
} from '../api/client';
import { buildAcceptUrl } from '../utils/inviteUrl';
import { useAdminScope } from '../hooks/useAdminScope';
import { bulkImportTemplateCsv, bulkRowResultKey, similarNewDepartments } from '../utils/bulkImport';

/**
 * CSV で一括登録（課題 #94 / #51）。
 *
 * 1. CSV を選び「内容を確認する」で、取り込まずに各行の結果と新しく作られる部署を見る
 * 2. 確かめてから「招待する」で取り込む。登録できる行だけを招待する
 *
 * 確認では、新しく作られる部署が既存のどれに似ているかも出す。「新しく作られます」だけでは、
 * それが正しいのか表記ゆれなのか判断できず、同じ部署を二重に作ってしまうため。
 * 利用者は、本人が招待のリンクを開き、同意してパスワードを設定した時点で作られる。
 */
export default function AdminUsersImport() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const adminScope = useAdminScope();
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** 確認したときの中身。取り込みでは同じ中身を送る（確認後にファイルが変わっても取り違えない） */
  const [file, setFile] = useState<File | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<BulkImportPreviewResponse | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<BulkImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedRow, setCopiedRow] = useState<number | null>(null);

  const roleLabel = (role: string) => t(`admin.usersRoleOption.${role}`, { defaultValue: role });
  const resultLabel = (r: string) => {
    const key = bulkRowResultKey(r);
    return key ? t(`admin.usersImport.result.${key}`) : r;
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    setPreview(null);
    setResult(null);
    setError(null);
    if (!picked) {
      setFile(null);
      return;
    }
    // 中身をこの時点で読み取っておく。確認のあとにファイルが書き換わっても、確認した中身を取り込む
    const content = await picked.arrayBuffer();
    setFile(new File([content], picked.name, { type: picked.type || 'text/csv' }));
  };

  const handlePreview = async () => {
    const token = getToken();
    if (!token || !file || previewing) return;
    setPreviewing(true);
    setError(null);
    setPreview(null);
    setResult(null);
    try {
      setPreview(await adminUsersBulkImportApi.preview(file, token));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    const token = getToken();
    if (!token || !file || !preview || importing) return;
    const similar = similarNewDepartments(preview);
    const lines = [
      t('admin.usersImport.confirm.invite', { count: preview.ready_rows }),
      preview.new_departments.length > 0
        ? t('admin.usersImport.confirm.newDepartments', { count: preview.new_departments.length })
        : null,
      similar.length > 0 ? t('admin.usersImport.confirm.similar', { count: similar.length }) : null,
      t('admin.usersImport.confirm.ok'),
    ].filter(Boolean);
    if (!window.confirm(lines.join('\n'))) return;
    setImporting(true);
    setError(null);
    try {
      setResult(await adminUsersBulkImportApi.import(file, token));
      setPreview(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([bulkImportTemplateCsv()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users-import.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const rowsTable = (rows: (BulkImportRow | BulkImportResultRow)[], testId: string) => (
    <table className="admin-table admin-users-import-table" data-testid={testId}>
      <thead>
        <tr>
          <th scope="col">{t('admin.usersImport.column.row')}</th>
          <th scope="col">{t('auth.email')}</th>
          <th scope="col">{t('admin.usersImport.column.displayName')}</th>
          <th scope="col">{t('admin.usersRole')}</th>
          <th scope="col">{t('admin.usersImport.column.department')}</th>
          <th scope="col">{t('admin.usersImport.column.result')}</th>
          <th scope="col">{t('admin.usersImport.column.reasons')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const resultRow = r as BulkImportResultRow;
          const acceptUrl =
            resultRow.email_sent === false && resultRow.invite_token ? buildAcceptUrl(resultRow.invite_token) : null;
          return (
            <tr key={r.row} data-testid="import-row" className={`is-${r.result}`}>
              <td>{r.row}</td>
              <td>{r.email}</td>
              <td>{r.display_name ?? ''}</td>
              <td>{roleLabel(r.role)}</td>
              <td>{r.department_name ?? ''}</td>
              <td>
                <span className={`admin-users-import-badge is-${r.result}`}>{resultLabel(r.result)}</span>
              </td>
              <td>
                {(r.reasons ?? []).join('／')}
                {/* 招待メールを送れなかった行は、リンクを別の方法で本人に渡せるようにする（#54 と同じ） */}
                {resultRow.email_sent === false && (
                  <div className="admin-users-import-mail-failed" data-testid="import-mail-failed">
                    <p>{t('admin.usersImport.mailFailed', { reason: resultRow.email_error ?? '' })}</p>
                    {acceptUrl && (
                      <>
                        <code className="admin-invite-url-value">{acceptUrl}</code>
                        <button
                          type="button"
                          className="admin-btn-text"
                          onClick={() => {
                            navigator.clipboard?.writeText(acceptUrl).then(
                              () => setCopiedRow(r.row),
                              () => setCopiedRow(null)
                            );
                          }}
                        >
                          {copiedRow === r.row ? t('admin.usersInviteUrl.copied') : t('admin.usersInviteUrl.copy')}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className="page admin-users-import-page">
      <h1>{t('admin.usersImport.title')}</h1>
      <p className="admin-page-intro">{t('admin.usersImport.intro')}</p>

      <section className="admin-card">
        <h2>{t('admin.usersImport.format.title')}</h2>
        <ul className="admin-users-import-format">
          <li>{t('admin.usersImport.format.columns')}</li>
          <li>{t('admin.usersImport.format.role')}</li>
          <li>{t('admin.usersImport.format.limit')}</li>
          <li>{t('admin.usersImport.format.save')}</li>
        </ul>
        {adminScope === 'restricted' && (
          <p className="admin-form-hint" data-testid="import-restricted-note">
            {t('admin.usersImport.format.restricted')}
          </p>
        )}
        <button type="button" className="admin-btn-text" onClick={downloadTemplate}>
          {t('admin.usersImport.template')}
        </button>
      </section>

      <section className="admin-card admin-form">
        <div className="admin-form-group">
          <label htmlFor="users-import-file">{t('admin.usersImport.file')}</label>
          <input
            id="users-import-file"
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            disabled={previewing || importing}
          />
        </div>
        <button
          type="button"
          className="admin-btn-primary"
          onClick={handlePreview}
          disabled={!file || previewing || importing}
        >
          {previewing ? t('admin.usersImport.previewing') : t('admin.usersImport.preview')}
        </button>
        {error && (
          <p className="admin-form-error" role="alert">
            {error}
          </p>
        )}
      </section>

      {preview && (
        <section className="admin-card" data-testid="import-preview">
          <h2>{t('admin.usersImport.previewTitle')}</h2>
          <p data-testid="import-preview-summary">
            {t('admin.usersImport.previewSummary', {
              total: preview.total_rows,
              ready: preview.ready_rows,
              skipped: preview.skipped_rows,
              error: preview.error_rows,
            })}
          </p>

          {preview.new_departments.length > 0 && (
            <div className="admin-users-import-departments" data-testid="import-new-departments">
              <h3>{t('admin.usersImport.newDepartments')}</h3>
              <ul>
                {preview.new_departments.map((d) => (
                  <li key={d.name}>
                    {t('admin.usersImport.newDepartment', { name: d.name, count: d.row_count })}
                    {(d.similar_existing ?? []).map((s) => (
                      <p key={s.department_id} className="admin-users-import-similar" data-testid="import-similar">
                        {t('admin.usersImport.similar', { name: s.name, reason: s.reason })}
                      </p>
                    ))}
                    {/* CSV の中のほかの新しい部署と似ている（課題 #94）。両方とも新規だと既存との比較では気づけない */}
                    {(d.similar_new ?? []).map((s) => (
                      <p key={`new-${s.name}`} className="admin-users-import-similar" data-testid="import-similar-new">
                        {t('admin.usersImport.similarNew', { name: s.name, reason: s.reason })}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.warnings.length > 0 && (
            <ul className="admin-users-import-warnings">
              {preview.warnings.map((w, i) => (
                <li key={`${w.row}-${i}`}>{t('admin.usersImport.warning', { row: w.row, message: w.message })}</li>
              ))}
            </ul>
          )}

          {rowsTable(preview.rows, 'import-preview-rows')}

          {preview.ready_rows > 0 ? (
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handleImport}
              disabled={importing}
              data-testid="import-run"
            >
              {importing
                ? t('admin.usersImport.importing')
                : t('admin.usersImport.import', { count: preview.ready_rows })}
            </button>
          ) : (
            <p className="admin-form-hint">{t('admin.usersImport.nothingToImport')}</p>
          )}
        </section>
      )}

      {result && (
        <section className="admin-card" data-testid="import-result">
          <h2>{t('admin.usersImport.resultTitle')}</h2>
          <p data-testid="import-result-summary">
            {t('admin.usersImport.resultSummary', {
              invited: result.invited,
              skipped: result.skipped,
              error: result.errors,
            })}
          </p>
          {result.email_failed > 0 && (
            <p className="admin-form-error" role="alert">
              {t('admin.usersImport.resultMailFailed', { count: result.email_failed })}
            </p>
          )}
          {result.created_departments.length > 0 && (
            <p>
              {t('admin.usersImport.createdDepartments', {
                names: result.created_departments.map((d) => d.name).join('、'),
              })}
            </p>
          )}
          {rowsTable(result.rows, 'import-result-rows')}
          <button type="button" className="admin-btn-text" onClick={reset}>
            {t('admin.usersImport.again')}
          </button>
        </section>
      )}

      <p className="admin-back">
        <Link to="/admin/users">{t('common.back')} → {t('admin.usersManage')}</Link>
      </p>
    </div>
  );
}
